import { AccessToken } from "../../../core/models/auth/access-token.model";
import { UtilService } from "../../../core/services/util.service";
import { getApiBaseUrl } from "../../../core/config/runtimeConfig";
import { User } from "../models/user.model";
import { useAuthStore } from "../../../core/store/auth.store";

export class AuthService {
  private static readonly utils: UtilService = UtilService.GetInstance();

  static async Request<T>(
    method: string,
    endpoint: string,
    params: Record<string, unknown> = {},
    onError: (error: Error) => void,
  ): Promise<T> {
    const url = `${getApiBaseUrl()}${endpoint}`;
    const auth = useAuthStore.getState().auth;
    const token = auth?.accessToken?.accessToken;

    let options: RequestInit = {
      method: method,
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    };
    if (method === "GET" || method === "HEAD") {
      options.body = undefined;
    } else {
      options.body = JSON.stringify(params);
    }
    try {
      const response = await fetch(url, options);
      const text = await response.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = {};
      }
      if (!response.ok) {
        throw new Error(data.message || "Something went wrong");
      }

      return data as T;
    } catch (error: unknown) {
      if (error instanceof Error) {
        onError(error);
      } else {
        onError(new Error("Unknown error occurred"));
      }
      throw error;
    }
  }

  static async Login(user: User): Promise<AccessToken> {
    const loginPayload = {
      username: user.username,
      password: user.password,
      totp: user.totp || undefined,
    };
    
    return await this.Request(
      "POST",
      "/public/login",
      loginPayload,
      (error: Error) => {
        // Don't show alerts for OTP-related errors, let the Login component handle them
        if (error.message && (
          error.message.includes("OTP_REQUIRED") ||
          error.message.includes("INVALID_OTP") ||
          error.message.includes("ACCOUNT_SETUP_REQUIRED")
        )) {
          // Let the Login component handle OTP-related errors
          return;
        }
        
        // Debug: Log the actual error message to see what we're getting
        console.log("Login error message:", error.message);
        
        if (
          error.message.includes("UNAUTHORIZED") ||
          error.message.includes("Invalid user credentials") ||
          error.message.includes("401") ||
          error.message.includes("Unauthorized") ||
          error.message.includes("invalid credentials") ||
          error.message.includes("authentication failed") ||
          error.message.includes("login failed") ||
          error.message.includes("Bad credentials") ||
          error.message.includes("Invalid username or password") ||
          error.message.includes("Authentication failed") ||
          error.message.includes("Access denied") ||
          error.message.includes("Invalid login")
        ) {
          UtilService.Alert(
            "Login Failed",
            "error",
            "Invalid username or password. Please check your credentials."
          );
        } else {
          // For now, let's show the preferred message for any login error
          // until we identify the exact error pattern
          UtilService.Alert(
            "Login Failed",
            "error",
            "Invalid username or password. Please check your credentials."
          );
        }
      },
    );
  }

  static async GetInfo(
    verificationCode: string,
  ): Promise<{ email: string; id: string }> {
    try {
      const response = await this.Request<{ email: string; id: string }>(
        "GET",
        `/public/user-registrations?code=${verificationCode}`,
        {},
        (error: Error) => {
          console.error("API Error:", error.message);
          if (error.message.includes("NOT FOUND")) {
            UtilService.Alert("Error", "error", "Invalid verification code");
          } else {
            UtilService.Alert("Error", "error", "Internal Server Error");
          }
        },
      );
      return response;
    } catch (error) {
      console.error("Error Fetching Info:", error);
      throw error;
    }
  }
  static async SetupPassword(
    id: string,
    email: string,
    code: string,
    password: string,
  ): Promise<AccessToken> {
    const payload = {
      email,
      verificationCode: code,
      password,
    };
    return await this.Request(
      "POST",
      `/public/user-registrations/${id}`,
      payload,
      (error: Error) => {
        console.error("API Error:", error.message);
        UtilService.Alert("Important!", "info", "Internal Server Error");
      },
    );
  }

  static async ChangePassword(
    companyId: string,
    id: string,
    password: string,
  ): Promise<AccessToken> {
    const payload = {
      password,
    };
    return await this.Request(
      "PATCH",
      `/companies/${companyId}/users/${id}/password`,
      payload,
      (error: Error) => {
        console.error("API Error:", error.message);
        UtilService.Alert("Important!", "info", "Internal Server Error");
      },
    );
  }

  static async RequestPasswordRecovery(username: string): Promise<void> {
    return await this.Request(
      "POST",
      `/public/forgot-password?username=${encodeURIComponent(username)}`,
      {},
      (error: Error) => {
        console.error("API Error:", error.message);
        if (error.message.includes("NOT FOUND")) {
          UtilService.Alert("Error", "error", "Username not found");
        } else {
          UtilService.Alert("Error", "error", "Unable to process password recovery. Please try again later.");
        }
      },
    );
  }

  static async CheckOtpStatus(username: string): Promise<{ username: string; needsOtpSetup: boolean; hasOtpConfigured: boolean; message: string }> {
    return await this.Request(
      "GET",
      `/public/otp-status/${encodeURIComponent(username)}`,
      {},
      (error: Error) => {
        console.error("API Error:", error.message);
        UtilService.Alert("Error", "error", "Unable to check OTP status. Please try again later.");
      },
    );
  }

  static async SetupOtp(username: string): Promise<{ message: string; username: string }> {
    return await this.Request(
      "POST",
      "/public/setup-otp",
      { username },
      (error: Error) => {
        console.error("API Error:", error.message);
        UtilService.Alert("Error", "error", "Unable to setup OTP. Please try again later.");
      },
    );
  }

  static async CheckSecurityAvailability(username: string): Promise<{ username: string; shouldShow: boolean; message: string }> {
    return await this.Request(
      "GET",
      `/public/security-availability/${encodeURIComponent(username)}`,
      {},
      (error: Error) => {
        console.error("API Error:", error.message);
        UtilService.Alert("Error", "error", "Unable to check security availability. Please try again later.");
      },
    );
  }

  static async CheckTotpStatus(username: string): Promise<{ username: string; hasTotpConfigured: boolean }> {
    return await this.Request(
      "GET",
      `/public/totp-status/${encodeURIComponent(username)}`,
      {},
      (error: Error) => {
        console.error("API Error:", error.message);
        // Don't show alert for TOTP check errors, just log them
      },
    );
  }

  static async CleanupSecurityAvailabilityForTotpUser(username: string): Promise<{ message: string; username: string }> {
    return await this.Request(
      "DELETE",
      `/public/security-availability/cleanup/${encodeURIComponent(username)}`,
      {},
      (error: Error) => {
        console.error("Security availability cleanup error:", error.message);
        // Don't show alert for cleanup errors as this is a background operation
      },
    );
  }

  static async MarkSecurityAvailabilityAsShown(username: string, intervalDays?: number): Promise<{ message: string; username: string }> {
    return await this.Request(
      "POST",
      "/public/security-availability/mark-shown",
      { username, intervalDays },
      (error: Error) => {
        console.error("API Error:", error.message);
        UtilService.Alert("Error", "error", "Unable to mark security availability as shown. Please try again later.");
      },
    );
  }
}
